import { CheckOutlined, RedoOutlined } from '@ant-design/icons';

import { Button, Col, Empty, Input, Radio, Row, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { MapContainer, Marker, Tooltip as MapTooltip } from 'react-leaflet';

import lockerBlue from '../../assets/img/lockerBlue.png';
import lockerRed from '../../assets/img/lockerRed.png';
import { AddOrderDeliveryContext } from '../../views/orderDelivery/Add/AddOrderDeliveryContext';
import {
  isValidAddressUnits,
  mapAddressToUnits,
} from '../../views/orderDelivery/Add/addressUtils';
import CabinetLockerMarker from './CabinetLockerMarker';
import icon from './constants';
import { BaseLayers, MapCenterView, MapClickHandler, toLatLon } from './mapParts';
import './style.less';

const { Text } = Typography;

const CABINET_URL = '/api/v1.0/cabinet/listCabinetsWithGeo';
const DEFAULT_CENTER = { lat: 47.9123565, lon: 106.9151432 };

/** Газрын зураг болон хайлтын хэсгийн өндөр — AddressMap-тай нэг хэмжээ */
const PANEL_HEIGHT = 320;
const MAX_WIDTH = 960;

/** Хайлтын талбарын өндөр + доорх зай */
const SEARCH_OFFSET = 40;

/** Кодоор хайлт эхлэх тэмдэгтийн доод хязгаар */
const MIN_SEARCH_LENGTH = 4;

const CabinetMap = (props) => {
  const { lockers, setLockers, selectedDeliveryAddress, setSelectedDeliveryAddress, setAddressArray } =
    useContext(AddOrderDeliveryContext);

  const [searchKey, setSearchKey] = useState('');
  const [searching, setSearching] = useState(false);
  // Leaflet-д контейнер хэмжээтэй болсны дараа mount хийнэ (эс бөгөөс сааралдана)
  const [ready, setReady] = useState(false);
  // Сонголт биш, зөвхөн зургийн харагдац
  const [center, setCenter] = useState(selectedDeliveryAddress?.location ?? DEFAULT_CENTER);

  const selectedId = props.selectedLocker?._id ?? props.selectedBox?._id;

  useEffect(() => {
    setReady(true);
  }, []);

  const fetchCabinets = (params) => {
    setSearching(true);
    axios
      .get(CABINET_URL, { params })
      .then(({ data = {} }) => {
        setLockers(data?.result?.length ? data.result : []);
      })
      .catch((err) => {
        console.error('listCabinetsWithGeo: ', err);
      })
      .finally(() => setSearching(false));
  };

  const fetchNearby = ({ lat, lon }) => fetchCabinets({ lat, lon, type: 'geo' });

  // Эхлэхэд төвийн орчмын PP Box-уудыг харуулна — жагсаалт хоосон нээгдэхгүй
  useEffect(() => {
    if (!lockers?.length) fetchNearby(center);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Газрын зураг дээр дарсан цэг — зөвхөн орчмын PP Box-уудыг гаргана.
   * Сонголтыг хөндөхгүй (дарах нь сонгосон гэсэн үг биш).
   */
  const onMapClick = ({ lat, lng }) => {
    setCenter({ lat, lon: lng });
    fetchNearby({ lat, lon: lng });
  };

  /** Маркер эсвэл жагсаалтаас PP Box сонгох */
  const selectLocker = (locker) => {
    const { lat, lon } = toLatLon(locker?.location?.coordinates);
    if (lat == null || lon == null) return;

    setCenter({ lat, lon });
    setSelectedDeliveryAddress({ ...locker, location: { lat, lon } });
    props.setSelectedLocker?.(locker);
    props.setSelectedBox?.(locker);

    // Хянах хуудсанд хот/дүүрэг/хороо бүрэн харагдахын тулд
    const units = mapAddressToUnits(locker?.address);
    if (isValidAddressUnits(units)) setAddressArray?.(units);
  };

  const onSearchChange = (e) => {
    const { value } = e.target;
    setSearchKey(value);

    if (value.length >= MIN_SEARCH_LENGTH) fetchCabinets({ code: value });
    else if (!value) fetchNearby(center);
  };

  const onSearch = (value) => {
    if (value) fetchCabinets({ code: value });
    else fetchNearby(center);
  };

  const resetView = () => {
    const target = selectedDeliveryAddress?.location ?? DEFAULT_CENTER;
    setCenter(target);
    setSearchKey('');
    fetchNearby(target);
  };

  return (
    <div className="pp_map_panel" style={{ maxWidth: MAX_WIDTH }}>
      <Text style={{ display: 'block', marginBottom: 8 }}>
        <Text type="danger">*</Text> PP Box сонгоно уу
      </Text>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <div className="pp_map_panel__canvas" style={{ height: PANEL_HEIGHT }}>
            {ready && (
              <MapContainer
                center={[center.lat, center.lon]}
                zoom={16}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                style={{ height: '100%', width: '100%' }}
              >
                <BaseLayers />
                <MapClickHandler onClick={onMapClick} />

                {lockers?.map((locker) => {
                  const { lat, lon } = toLatLon(locker?.location?.coordinates);
                  if (lat == null || lon == null) return null;

                  return (
                    <CabinetLockerMarker
                      key={locker._id}
                      position={[lat, lon]}
                      data={locker}
                      eventHandlers={{ click: () => selectLocker(locker) }}
                    >
                      <MapTooltip>
                        <div className="tool_tip_name">{locker.name}</div>
                        <div>{locker.addressName}</div>
                        <div>
                          {locker.lastLogin
                            ? moment(locker.lastLogin).format('YYYY/MM/DD - HH:mm:ss')
                            : '-'}
                        </div>
                      </MapTooltip>
                    </CabinetLockerMarker>
                  );
                })}

                <Marker position={[center.lat, center.lon]} icon={icon} />
                <MapCenterView center={center} />
              </MapContainer>
            )}

            <Tooltip title="Сонгосон PP Box руу буцах" placement="left">
              <Button
                className="pp_map_panel__reset"
                icon={<RedoOutlined rotate={270} />}
                onClick={resetView}
              />
            </Tooltip>
          </div>
        </Col>

        <Col xs={24} lg={10}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Input.Search
              placeholder="PP Box-ын кодоор хайх"
              enterButton="Хайх"
              allowClear
              loading={searching}
              value={searchKey}
              onChange={onSearchChange}
              onSearch={onSearch}
            />

            <div
              className="pp_map_panel__results"
              style={{ height: PANEL_HEIGHT - SEARCH_OFFSET }}
            >
              {/* Хайлт/ойролцоох PP Box татаж байх үед хоосон төлөв андуурагдахгүй */}
              <Spin spinning={searching}>
                {lockers?.length ? (
                  <Radio.Group
                    value={selectedId}
                    onChange={(e) => {
                      const locker = lockers.find((item) => item._id === e.target.value);
                      if (locker) selectLocker(locker);
                    }}
                    className="pp_map_panel__radios"
                  >
                    {lockers.map((locker) => {
                      const isFull = locker.serviceStatus === 'full';

                      return (
                        <Radio key={locker._id} value={locker._id}>
                          <Space size={10}>
                            {/* Газрын зураг дээрх маркертай ижил өнгө — цэнхэр сул, улаан дүүрэн */}
                            <img
                              className="pp_map_panel__locker"
                              src={isFull ? lockerRed : lockerBlue}
                              alt={isFull ? 'Дүүрэн' : 'Сул'}
                            />
                            <Space direction="vertical" size={0}>
                              <Text>{locker.name}</Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {locker.addressName}
                              </Text>
                            </Space>
                          </Space>

                          <Space size={6}>
                            {isFull && (
                              <Tag color="error" className="pp_map_panel__tag">
                                Дүүрэн
                              </Tag>
                            )}
                            {selectedId === locker._id && (
                              <CheckOutlined className="pp_map_panel__check" />
                            )}
                          </Space>
                        </Radio>
                      );
                    })}
                  </Radio.Group>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Газрын зураг дээр дарж эсвэл кодоор хайж PP Box-оо сонгоно уу.
                      </Text>
                    }
                    style={{ margin: 0, paddingTop: 48 }}
                  />
                )}
              </Spin>
            </div>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default CabinetMap;
